import type {
  ApplicationFormData,
  CertificationDraft,
  EducationEntry,
  WorkExperience,
} from "./types";

/**
 * Field limits the API enforces on `POST public/jobs/{id}/apply` (and the
 * org-scoped twin). Mirrors `applyToJobSchema` and its nested work-experience,
 * education and certification schemas in ai2aim-ems-api
 * (`src/validations/candidate.validation.ts`, `CandidateValidationRules` in
 * `src/const/candidate.constants.ts`). Change them together: a looser ceiling
 * here only moves the failure back to the Submit toast.
 *
 * Lengths are measured on the value the form actually sends (trimmed), with
 * `String.length`, which is what Joi's `string.max` / `string.min` count.
 */
export const APPLICATION_LIMITS = {
  // Names stay at 50: hiring copies them into employees.first_name/last_name (varchar(50)).
  FIRST_NAME_MAX: 50,
  LAST_NAME_MAX: 50,
  EMAIL_MAX: 255,
  HEADLINE_MAX: 255,
  PROFESSIONAL_SUMMARY_MAX: 5000,
  RESUME_URL_MAX: 500,
  EXPERIENCE_YEARS_MIN: 0,
  EXPERIENCE_YEARS_MAX: 70,
  EXPECTED_SALARY_MAX: 100,

  COMPANY_MIN: 2,
  COMPANY_MAX: 150,
  POSITION_MIN: 2,
  POSITION_MAX: 150,
  WORK_DESCRIPTION_MAX: 4000,

  INSTITUTION_MIN: 2,
  INSTITUTION_MAX: 150,
  DEGREE_MIN: 2,
  DEGREE_MAX: 100,
  FIELD_OF_STUDY_MIN: 2,
  FIELD_OF_STUDY_MAX: 100,
  YEAR_MIN: 1900,
  YEAR_MAX: 2100,

  CERT_NAME_MIN: 2,
  CERT_NAME_MAX: 150,
  CERT_ISSUER_MIN: 2,
  CERT_ISSUER_MAX: 150,
  CERT_AWARDED_TO_MIN: 2,
  CERT_AWARDED_TO_MAX: 100,
  CERT_CREDENTIAL_ID_MAX: 100,

  /** skills: at most 50, each at most 100 characters (T100926001; the column is varchar(255) per skill). */
  SKILLS_MAX: 50,
  SKILL_MAX: 100,

  /** workExperience / educationEntries / certificationEntries are each `.max(20)`. */
  MAX_ENTRIES: 20,
} as const;

/**
 * One problem with one field.
 *
 * `deferred` problems (a required field still empty, a value still too short,
 * an email or year still being typed) stay hidden until the candidate tries to
 * leave the step, so the form does not shout on the first keystroke. Every
 * other problem — over a character limit, out of range, not a valid link,
 * dates in the wrong order — shows the moment it exists.
 */
export interface FieldIssue {
  message: string;
  deferred?: boolean;
}

export type FieldIssues<K extends string> = Partial<Record<K, FieldIssue>>;
export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/** The messages a field should display now. */
export function visibleErrors<K extends string>(
  issues: FieldIssues<K>,
  revealDeferred: boolean,
): FieldErrors<K> {
  const out: FieldErrors<K> = {};
  for (const key of Object.keys(issues) as K[]) {
    const issue = issues[key];
    if (issue && (revealDeferred || !issue.deferred)) out[key] = issue.message;
  }
  return out;
}

export function issueMessages<K extends string>(issues: FieldIssues<K>): string[] {
  return (Object.values(issues) as FieldIssue[]).map((i) => i.message);
}

export function hasIssues<K extends string>(issues: FieldIssues<K>): boolean {
  return Object.keys(issues).length > 0;
}

const required = (label: string): FieldIssue => ({ message: `${label} is required`, deferred: true });
const tooShort = (label: string, min: number): FieldIssue => ({
  message: `${label} must be at least ${min} characters`,
  deferred: true,
});
const tooLong = (label: string, max: number, length: number): FieldIssue => ({
  message: `${label} must be at most ${max} characters (currently ${length})`,
});

/** Length rules for one text value. `min` applies only once something is typed. */
function checkText(
  raw: string | null | undefined,
  label: string,
  opts: { required?: boolean; min?: number; max: number },
): FieldIssue | undefined {
  const value = (raw || "").trim();
  if (!value) return opts.required ? required(label) : undefined;
  if (value.length > opts.max) return tooLong(label, opts.max, value.length);
  if (opts.min && value.length < opts.min) return tooShort(label, opts.min);
  return undefined;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const YEAR_MONTH = /^\d{4}-\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * Expected salary exactly as it is sent: a single number becomes a formatted
 * currency string ("$120,000"); anything already containing a dash is sent as
 * typed. Shared with the submit path so the length check measures the real
 * payload.
 */
export function buildExpectedSalary(raw: string, currency: string): string {
  if (!raw) return raw;
  if (raw.includes(" - ") || raw.includes("-")) return raw;
  const salaryNum = parseFloat(raw);
  if (Number.isNaN(salaryNum)) return raw;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(salaryNum);
}

export type PersonalInfoField =
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "expectedSalary";

export function validatePersonalInfo(
  formData: ApplicationFormData,
  lockedEmail?: string | null,
): FieldIssues<PersonalInfoField> {
  const L = APPLICATION_LIMITS;
  const issues: FieldIssues<PersonalInfoField> = {};

  const firstName = checkText(formData.firstName, "First name", { required: true, max: L.FIRST_NAME_MAX });
  if (firstName) issues.firstName = firstName;
  const lastName = checkText(formData.lastName, "Last name", { required: true, max: L.LAST_NAME_MAX });
  if (lastName) issues.lastName = lastName;

  // An account email is locked and already valid; only a typed one is checked.
  if (!lockedEmail) {
    const email = (formData.email || "").trim();
    if (!email) issues.email = required("Email");
    else if (email.length > L.EMAIL_MAX) issues.email = tooLong("Email", L.EMAIL_MAX, email.length);
    else if (!EMAIL_PATTERN.test(email))
      issues.email = { message: "Enter a valid email address, like name@example.com", deferred: true };
  }

  if (!(formData.phone || "").trim()) issues.phone = required("Phone number");

  const salaryRaw = (formData.expectedSalary || "").trim();
  const salaryNum = parseFloat(salaryRaw);
  if (!salaryRaw) {
    issues.expectedSalary = required("Expected salary");
  } else if (Number.isNaN(salaryNum) || salaryNum <= 0) {
    issues.expectedSalary = { message: "Expected salary must be a number greater than 0" };
  } else {
    const sent = buildExpectedSalary(salaryRaw, formData.salaryCurrency);
    if (sent.length > L.EXPECTED_SALARY_MAX) {
      issues.expectedSalary = { message: "Expected salary is too large" };
    }
  }

  return issues;
}

export type SummaryField = "headline" | "professionalSummary" | "totalExperienceYears";

export function validateSummary(formData: ApplicationFormData): FieldIssues<SummaryField> {
  const L = APPLICATION_LIMITS;
  const issues: FieldIssues<SummaryField> = {};

  const headline = checkText(formData.headline, "Headline", { max: L.HEADLINE_MAX });
  if (headline) issues.headline = headline;

  const summary = checkText(formData.professionalSummary, "Professional summary", {
    required: true,
    max: L.PROFESSIONAL_SUMMARY_MAX,
  });
  if (summary) issues.professionalSummary = summary;

  const yearsRaw = String(formData.totalExperienceYears ?? "").trim();
  if (yearsRaw) {
    const years = Number(yearsRaw);
    if (Number.isNaN(years)) {
      issues.totalExperienceYears = { message: "Years of experience must be a number" };
    } else if (years < L.EXPERIENCE_YEARS_MIN || years > L.EXPERIENCE_YEARS_MAX) {
      issues.totalExperienceYears = {
        message: `Years of experience must be between ${L.EXPERIENCE_YEARS_MIN} and ${L.EXPERIENCE_YEARS_MAX}`,
      };
    }
  }

  return issues;
}

export type ExperienceField = "company" | "position" | "startDate" | "endDate" | "description";

export function validateExperienceEntry(exp: WorkExperience): FieldIssues<ExperienceField> {
  const L = APPLICATION_LIMITS;
  const issues: FieldIssues<ExperienceField> = {};

  const company = checkText(exp.company, "Company", { required: true, min: L.COMPANY_MIN, max: L.COMPANY_MAX });
  if (company) issues.company = company;
  const position = checkText(exp.position, "Position", {
    required: true,
    min: L.POSITION_MIN,
    max: L.POSITION_MAX,
  });
  if (position) issues.position = position;
  const description = checkText(exp.description, "Description", { max: L.WORK_DESCRIPTION_MAX });
  if (description) issues.description = description;

  const start = (exp.startDate || "").trim();
  const end = exp.isCurrent ? "" : (exp.endDate || "").trim();
  if (!start) issues.startDate = required("Start date");
  if (!exp.isCurrent && !end) issues.endDate = required("End date");
  // `YYYY-MM` from <input type="month">, so string order is date order. Values
  // in any other shape (rare resume-parser output) are left to the API.
  if (YEAR_MONTH.test(start) && YEAR_MONTH.test(end) && end.slice(0, 7) < start.slice(0, 7)) {
    issues.endDate = { message: "End date must be on or after the start date" };
  }

  return issues;
}

export type EducationField = "institution" | "degree" | "fieldOfStudy" | "endYear";

export function validateEducationEntry(edu: EducationEntry): FieldIssues<EducationField> {
  const L = APPLICATION_LIMITS;
  const issues: FieldIssues<EducationField> = {};

  const institution = checkText(edu.institution, "Institution", {
    required: true,
    min: L.INSTITUTION_MIN,
    max: L.INSTITUTION_MAX,
  });
  if (institution) issues.institution = institution;
  const degree = checkText(edu.degree, "Degree", { min: L.DEGREE_MIN, max: L.DEGREE_MAX });
  if (degree) issues.degree = degree;
  const fieldOfStudy = checkText(edu.fieldOfStudy, "Field of study", {
    required: true,
    min: L.FIELD_OF_STUDY_MIN,
    max: L.FIELD_OF_STUDY_MAX,
  });
  if (fieldOfStudy) issues.fieldOfStudy = fieldOfStudy;

  const year = (edu.endYear || "").trim();
  if (!year) {
    issues.endYear = required("End year");
  } else if (!/^\d{4}$/.test(year)) {
    issues.endYear = { message: "Enter a 4-digit year", deferred: true };
  } else {
    const n = Number(year);
    if (n < L.YEAR_MIN || n > L.YEAR_MAX) {
      issues.endYear = { message: `End year must be between ${L.YEAR_MIN} and ${L.YEAR_MAX}` };
    }
  }

  return issues;
}

export type CertificationField = keyof CertificationDraft;

export function validateCertificationDraft(draft: CertificationDraft): FieldIssues<CertificationField> {
  const L = APPLICATION_LIMITS;
  const issues: FieldIssues<CertificationField> = {};

  const name = checkText(draft.name, "Certification name", {
    required: true,
    min: L.CERT_NAME_MIN,
    max: L.CERT_NAME_MAX,
  });
  if (name) issues.name = name;
  const issuer = checkText(draft.issuer, "Issuer", {
    required: true,
    min: L.CERT_ISSUER_MIN,
    max: L.CERT_ISSUER_MAX,
  });
  if (issuer) issues.issuer = issuer;
  const credentialId = checkText(draft.credentialId, "Credential ID", { max: L.CERT_CREDENTIAL_ID_MAX });
  if (credentialId) issues.credentialId = credentialId;
  const awardedTo = checkText(draft.awardedTo, "Awarded to", {
    min: L.CERT_AWARDED_TO_MIN,
    max: L.CERT_AWARDED_TO_MAX,
  });
  if (awardedTo) issues.awardedTo = awardedTo;

  if (!draft.issueDate) issues.issueDate = required("Issue date");
  if (!draft.expiryDate) issues.expiryDate = required("Expiry date");
  // `YYYY-MM-DD` from <input type="date">; the API requires expiry strictly later.
  if (
    ISO_DATE.test(draft.issueDate || "") &&
    ISO_DATE.test(draft.expiryDate || "") &&
    draft.expiryDate.slice(0, 10) <= draft.issueDate.slice(0, 10)
  ) {
    issues.expiryDate = { message: "Expiry date must be after the issue date" };
  }

  return issues;
}

/** Resume link: optional, but when present it must be an http(s) URL the API will accept. */
/** The skills list against the API's limits: how many, and each one's length. */
export function validateSkills(skills: string[]): FieldIssue | undefined {
  const L = APPLICATION_LIMITS;
  if (skills.length > L.SKILLS_MAX) {
    return { message: `List at most ${L.SKILLS_MAX} skills (you have ${skills.length})` };
  }
  const long = skills.find((skill) => skill.trim().length > L.SKILL_MAX);
  if (long) {
    const shown = long.trim().length > 40 ? `${long.trim().slice(0, 40)}…` : long.trim();
    return { message: `Each skill must be at most ${L.SKILL_MAX} characters ("${shown}" has ${long.trim().length})` };
  }
  return undefined;
}

export function validateResumeUrl(raw: string): FieldIssue | undefined {
  const url = (raw || "").trim();
  if (!url) return undefined;
  if (url.startsWith("blob:")) {
    return { message: "Local preview URLs cannot be used. Provide an external URL or upload the file." };
  }
  if (url.length > APPLICATION_LIMITS.RESUME_URL_MAX) {
    return tooLong("Resume URL", APPLICATION_LIMITS.RESUME_URL_MAX, url.length);
  }
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return { message: "Enter a link that starts with https://" };
    }
  } catch {
    return { message: "Enter a valid URL, like https://example.com/resume.pdf" };
  }
  return undefined;
}
